import { useMemo } from "react";
import { useSearchParams } from "react-router";

import PaginationIndicator from "components/PaginationIndicator";
import SearchInput from "components/SearchInput";
import { getSearchQuery } from "components/SearchInput/helpers";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import {
  EmptystateMagnetColored,
  EmptystateSearchNoResultsColored,
  EmptystateStacksColored,
} from "components/icons/generated";
import Box from "ds/components/Box";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import { SearchQueryOrderDirection } from "types/generated";
import CloudUsedBySection from "views/Account/Integrations/registry/shared/CloudUsedBySection";
import useLocalTableSort from "views/Account/Integrations/registry/shared/hooks/useLocalTableSort";

import { AwsIntegrationView } from "../gql";
import { INTEGRATIONS_AWS_USED_BY_TABLE_CELLS } from "./Cells";
import {
  AWS_USED_BY_AUTOATTACHED_FILTERS_STORAGE_KEY,
  AWS_USED_BY_FILTERS_STORAGE_KEY,
  AWS_USED_BY_MANUAL_FILTERS_STORAGE_KEY,
  SORT_CONFIG,
  USED_BY_COLUMN_ID,
} from "./constants";
import styles from "./styles.module.css";
import {
  useIntegrationsAwsUsedByAutoattachedTableStore,
  useIntegrationsAwsUsedByManualTableStore,
} from "./useAwsUsedByTableStore";
import { useFilteredStacks } from "./useFilteredStacks";

type IntegrationUsedByProps = {
  integration: AwsIntegrationView;
};

const IntegrationUsedBy = ({ integration }: IntegrationUsedByProps) => {
  const [searchParams] = useSearchParams();
  const searchQuery = getSearchQuery(searchParams);

  const manualAttachedStacks = useMemo(
    () => integration.attachedStacks.filter((stack) => !stack.isAutoattached),
    [integration.attachedStacks]
  );
  const autoattachedStacks = useMemo(
    () => integration.attachedStacks.filter((stack) => stack.isAutoattached),
    [integration.attachedStacks]
  );

  const { externalSortManagement: externalSortManagementManual, filterParams: filterParamsManual } =
    useLocalTableSort({
      localStorageKey: AWS_USED_BY_MANUAL_FILTERS_STORAGE_KEY,
      sortConfig: SORT_CONFIG,
      initialSortOption: USED_BY_COLUMN_ID.NAME,
      initialSortDirection: SearchQueryOrderDirection.Asc,
    });

  const {
    externalSortManagement: externalSortManagementAutoattached,
    filterParams: filterParamsAutoattached,
  } = useLocalTableSort({
    localStorageKey: AWS_USED_BY_AUTOATTACHED_FILTERS_STORAGE_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption: USED_BY_COLUMN_ID.NAME,
    initialSortDirection: SearchQueryOrderDirection.Asc,
  });

  const filteredManualStacks = useFilteredStacks(
    manualAttachedStacks,
    filterParamsManual,
    searchQuery
  );

  const filteredAutoattachedStacks = useFilteredStacks(
    autoattachedStacks,
    filterParamsAutoattached,
    searchQuery
  );
  const totalCount = integration.attachedStacks.length;
  const filteredManualCount = filteredManualStacks.length;
  const filteredAutoattachedCount = filteredAutoattachedStacks.length;
  const filteredCount = filteredManualCount + filteredAutoattachedCount;

  return (
    <>
      <PageInfoHeader title="Used by">
        {integration.attachedStacks.length > 0 && (
          <Box direction="row" align="center" gap="0 large">
            <PaginationIndicator
              currentCount={filteredCount}
              totalCount={totalCount}
              loading={false}
            />
            <SearchInput
              placeholder={SEARCH_PLACEHOLDERS.INTEGRATION_USED_BY}
              filtersOrderSettingsKey={AWS_USED_BY_FILTERS_STORAGE_KEY}
            />
          </Box>
        )}
      </PageInfoHeader>
      <Box direction="column" gap="large">
        <CloudUsedBySection
          stacks={filteredManualStacks}
          title="Attached directly"
          store={useIntegrationsAwsUsedByManualTableStore}
          integrationName={integration.name}
          cells={INTEGRATIONS_AWS_USED_BY_TABLE_CELLS}
          filtersStorageKey={AWS_USED_BY_MANUAL_FILTERS_STORAGE_KEY}
          sortConfig={SORT_CONFIG}
          externalSortManagement={externalSortManagementManual}
          emptyState={
            searchQuery ? (
              <EmptyState
                title="No results"
                icon={EmptystateSearchNoResultsColored}
                announce
                className={styles.emptyState}
              />
            ) : (
              <EmptyState
                title="Not attached to any projects yet"
                caption="This is where the stacks and modules that you have directly attached your cloud integration to will appear."
                icon={EmptystateStacksColored}
                className={styles.emptyState}
              />
            )
          }
        />

        <CloudUsedBySection
          stacks={filteredAutoattachedStacks}
          title="Auto-attached"
          store={useIntegrationsAwsUsedByAutoattachedTableStore}
          integrationName={integration.name}
          cells={INTEGRATIONS_AWS_USED_BY_TABLE_CELLS}
          filtersStorageKey={AWS_USED_BY_AUTOATTACHED_FILTERS_STORAGE_KEY}
          sortConfig={SORT_CONFIG}
          externalSortManagement={externalSortManagementAutoattached}
          emptyState={
            searchQuery ? (
              <EmptyState
                title="No results"
                icon={EmptystateSearchNoResultsColored}
                announce
                className={styles.emptyState}
              />
            ) : (
              <EmptyState
                title="Not auto-attached to any projects yet"
                caption="This is where auto-attached stacks and modules will appear. Use the autoattach label to attach cloud integrations to many stacks at the same time."
                icon={EmptystateMagnetColored}
                className={styles.emptyState}
              />
            )
          }
        />
      </Box>
    </>
  );
};

export default IntegrationUsedBy;
